import momentTimezone from 'moment-timezone';
import { Dispatch } from 'redux';
import {
    setCalendarData,
    setCalendarType,
} from '../actions/infiniteCalendarSeries';
import { TlzEnvironment } from '../api/constants';
import { getEventCalendarDates } from '../api/eventDates';
import { mapEventDatesToCalendar } from '../components/TimedEntrySeries/CalendarContainer/utils';
import { getBffRequestBaseParams } from '../selectors/bff';
import {
    getCalendarData,
    getTimezone,
} from '../selectors/infiniteCalendarSeries/infiniteCalendarSeries';
import { CheckoutState } from '../state';
import {
    CalendarData,
    DateEventStatus,
    ScrollableCalendarData,
    TransformedEventDate,
} from '../state/infiniteCalendarSeries';

export type EventDate = {
    date: string;
    checkoutEnabled: boolean;
    soldOut: boolean;
    highDemand?: boolean;
};

const shouldSkipLoading = (
    previousCalendarData: CalendarData,
    monthAndYear: { month: number; year: number },
): boolean => {
    if (Array.isArray(previousCalendarData)) {
        return true;
    }
    return Boolean(
        previousCalendarData?.years?.[monthAndYear.year]?.months?.[
            monthAndYear.month
        ],
    );
};

export const _initializeCalendarData = (
    previousCalendarData: CalendarData,
    monthAndYear: { month: number; year: number },
): ScrollableCalendarData => {
    const calendarData = (
        Array.isArray(previousCalendarData)
            ? { years: {} }
            : previousCalendarData
    ) as ScrollableCalendarData;

    calendarData.years = calendarData.years || {};
    calendarData.years[monthAndYear.year] = calendarData.years[
        monthAndYear.year
    ] || {
        months: {},
    };
    calendarData.years[monthAndYear.year].months[monthAndYear.month] =
        calendarData.years[monthAndYear.year].months[monthAndYear.month] || {
            isLoading: true,
            dates: {},
        };

    return calendarData;
};

export const _transformEventDates = (
    eventDates: EventDate[],
    timezone: string,
): Record<string, TransformedEventDate[]> => {
    const today = momentTimezone.tz(new Date(), timezone).startOf('day');
    return eventDates.reduce(
        (acc: Record<string, TransformedEventDate[]>, eventDateInfo) => {
            const date = new Date(eventDateInfo.date);
            const year = date.getFullYear();
            const month = date.getMonth() + 1;
            const key = `${year}_${month}`;

            if (!acc[key]) {
                acc[key] = [];
            }

            const eventDate = momentTimezone
                .tz(eventDateInfo.date, timezone)
                .startOf('day');

            let availability: DateEventStatus;

            if (eventDate.isBefore(today)) {
                availability = DateEventStatus.unavailable;
            } else {
                switch (true) {
                    case eventDateInfo.checkoutEnabled &&
                        eventDateInfo.highDemand:
                        availability = DateEventStatus.highDemand;
                        break;
                    case eventDateInfo.checkoutEnabled:
                        availability = DateEventStatus.available;
                        break;
                    case eventDateInfo.soldOut:
                        availability = DateEventStatus.soldOut;
                        break;
                    default:
                        // in this case tickets are not available for sale, but the date have slots with tickets, therefore it will be shown as available
                        availability = DateEventStatus.available;
                        break;
                }
            }

            acc[key].push({
                date: eventDateInfo.date,
                availability,
            });

            return acc;
        },
        {},
    );
};

export const _fetchAndUpdateCalendarData = async (
    dispatch: Dispatch<CheckoutState>,
    getState: () => CheckoutState,
    monthAndYear: { month: number; year: number },
    eventId: string,
    env: TlzEnvironment,
): Promise<void> => {
    try {
        const params = {
            eventId,
            env,
            month: monthAndYear.month + 1,
            year: monthAndYear.year,
        };

        const response = await getEventCalendarDates(params);

        const timezone = getTimezone(getState());
        const groupedEventDates = _transformEventDates(
            response.eventDates,
            timezone,
        );

        const previousCalendarData = getCalendarData(getState());
        if (Array.isArray(previousCalendarData)) {
            return;
        }
        const calendarData = mapEventDatesToCalendar(
            groupedEventDates,
            previousCalendarData as ScrollableCalendarData,
        );

        dispatch(setCalendarType(response.calendarType));
        dispatch(setCalendarData(calendarData));
    } catch (error) {
        console.error('Error fetching calendar data:', error);
    }
};

export const loadDatesForMonth =
    (monthAndYear: { month: number; year: number }) =>
    async (
        dispatch: Dispatch<CheckoutState>,
        getState: () => CheckoutState,
    ) => {
        const state = getState();
        const previousCalendarData = getCalendarData(state) || {};
        const { eventId, env } = getBffRequestBaseParams(state);

        if (shouldSkipLoading(previousCalendarData, monthAndYear)) {
            return;
        }

        const calendarData = _initializeCalendarData(
            previousCalendarData,
            monthAndYear,
        );
        dispatch(setCalendarData(calendarData));

        await _fetchAndUpdateCalendarData(
            dispatch,
            getState,
            monthAndYear,
            eventId,
            env,
        );
    };
